import React from 'react'
import styled, { css } from 'styled-components'
import { includes, xor } from 'lodash/fp'

// mechanizm blokuje focus (przy przechodzeniu klawiszem Tab) na potomkach focusable (nie wychodzimy poza potomkow). dodatkowo focusuje pierwszego potomka, ktory to umozliwia
export default class WithFocus extends React.Component {
  static defaultProps = {
    isActive: true,
  }

  constructor(props) {
    super(props)
    this.wrappedComponentRef = React.createRef()
    this.focusElement = null
    this.className = 'WithFocus'
    this.saveDocumentActiveElement()

    this.focusFirstFocusableElement = this.focusFirstFocusableElement.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleEvent = this.handleEvent.bind(this)
  }

  componentDidMount = () => {
    if (this.isMechanismActive() === true) {
      this.focusFirstFocusableElement()
    }

    window.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('click', this.handleEvent)
  }

  componentWillUnmount = () => {
    window.removeEventListener('keydown', this.handleKeydown)
    this.restoreDocumentActiveElement()
  }

  componentDidUpdate = (prevProps) => {
    if (prevProps.isActive === false && this.isMechanismActive() === true) {
      this.saveDocumentActiveElement()
      this.focusFirstFocusableElement()
    } else if (
      prevProps.isActive === true &&
      this.isMechanismActive() === false
    ) {
      this.restoreDocumentActiveElement()
    }
  }

  saveDocumentActiveElement = () => {
    this.focusElementBeforeFiltersOpen = document.activeElement
  }

  restoreDocumentActiveElement = () => {
    if (this.focusElementBeforeFiltersOpen != null) {
      this.focusElementBeforeFiltersOpen.focus()
    }
  }

  isMechanismActive = () => {
    return this.props.isActive === true && this.props.children != null
  }

  focusFirstFocusableElement = () => {
    if (this.wrappedComponentRef.current != null) {
      const allDescendants = this.wrappedComponentRef.current.querySelectorAll(
        '*'
      )

      for (const element of allDescendants) {
        if (isFocusable(element) === true) {
          setImmediate(() => {
            this.focusElement = element
            this.focusElement.focus()
          })

          break
        }
      }
    }
  }

  hasAnyWithFocusDescendantWithAnyFocusableElement = () => {
    if (this.wrappedComponentRef.current == null) {
      return false
    }

    const withFocusDescendants = Array.from(
      this.wrappedComponentRef.current.querySelectorAll(`.${this.className}`)
    )

    return withFocusDescendants.some((withFocusElement) => {
      const descendants = Array.from(withFocusElement.querySelectorAll('*'))

      return descendants.some((element) => {
        return isFocusable(element)
      })
    })
  }

  handleKeydown = (event) => {
    if (
      this.isMechanismActive() === false ||
      event.key !== 'Tab' ||
      this.wrappedComponentRef.current == null ||
      this.focusElement == null ||
      this.hasAnyWithFocusDescendantWithAnyFocusableElement() === true
    ) {
      return
    }

    event.preventDefault()

    const allFocusableDescendants = Array.from(
      this.wrappedComponentRef.current.querySelectorAll('*')
    ).filter((element) => isFocusable(element) === true)
    const focusElementIndex = allFocusableDescendants.findIndex(
      (element) => element === this.focusElement
    )

    if (focusElementIndex === -1) {
      this.focusElement = allFocusableDescendants[0]
    } else if (event.shiftKey === true) {
      this.focusElement =
        focusElementIndex === 0
          ? allFocusableDescendants[allFocusableDescendants.length - 1]
          : allFocusableDescendants[focusElementIndex - 1]
    } else {
      this.focusElement =
        focusElementIndex === allFocusableDescendants.length - 1
          ? allFocusableDescendants[0]
          : allFocusableDescendants[focusElementIndex + 1]
    }

    this.focusElement.focus()
  }

  handleEvent = (event) => {
    if (event.target.nodeName === 'BUTTON') {
      this.focusFirstFocusableElement()
    }
  }

  render = () => {
    return (
      <Wrapper
        ref={this.wrappedComponentRef}
        role="dialog"
        className={this.className}
        overflow={this.props.overflow}
      >
        {this.props.children}
      </Wrapper>
    )
  }
}

const Wrapper = styled.div`
  ${({ overflow }) =>
    overflow != null &&
    css`
      overflow: ${overflow};
    `};
`

// https://www.w3.org/WAI/ARIA/apg/example-index/js/utils.js
const isFocusable = (element) => {
  if (element.tabIndex < 0) {
    return false
  }

  if (element.disabled) {
    return false
  }

  switch (element.nodeName) {
    case 'A':
      return !!element.href && element.rel != 'ignore'
    case 'INPUT':
      return element.type != 'hidden'
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true
    default:
      return false
  }
}

export { isFocusable }
